<template>
  <section class="section">
    <div class="container">
      <!-- Service cards row -->
      <div class="services-row reveal">
        <article
          v-for="(service, i) in SERVICE_CARDS"
          :key="i"
          class="dark-card service-card"
        >
          <v-icon class="service-card__icon" size="32">{{ service.icon }}</v-icon>
          <h3 class="heading-md service-card__title">{{ service.title }}</h3>
          <span class="service-card__count">{{ service.count }}</span>
        </article>
      </div>

      <!-- Stats section -->
      <div class="stats-grid reveal reveal-delay-1">
        <div class="dark-card dark-card--glow stats-featured">
          <span class="stats-featured__number">4+</span>
          <h3 class="heading-md stats-featured__title">Years Of Experience</h3>
          <p class="body-sm">
            Front-end consultant delivering expert solutions to help businesses
            improve performance, efficiency, and user experience at scale.
          </p>
        </div>

        <div class="stats-cards">
          <div
            v-for="(stat, i) in STATS"
            :key="i"
            class="dark-card stats-card"
          >
            <span class="stats-card__value">{{ stat.value }}</span>
            <span class="stats-card__label">{{ stat.label }}</span>
          </div>
        </div>
      </div>

      <!-- Services list -->
      <div class="services-list-section">
        <div class="section-header reveal">
          <span class="eyebrow">Latest Service</span>
          <h2 class="heading-lg">Inspiring The World One Project</h2>
        </div>

        <div class="services-list">
          <article
            v-for="(item, i) in featuresData.slice(0, 6)"
            :key="i"
            class="service-row reveal"
            :class="`reveal-delay-${(i % 4) + 1}`"
          >
            <div class="service-row__icon-wrap">
              <v-icon size="24">{{ item.icon }}</v-icon>
            </div>
            <div class="service-row__content">
              <h3 class="heading-md">{{ item.title }}</h3>
              <span class="service-row__sub">{{ item.description.slice(0, 40) }}...</span>
            </div>
            <p class="body-sm service-row__desc">{{ item.description }}</p>
            <button type="button" class="service-row__cta" @click="goTo('portfolio')">
              READ MORE <v-icon size="14">mdi-chevron-right</v-icon>
            </button>
          </article>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import featuresData from "@/JsonFiles/features.json";
import { SERVICE_CARDS, STATS } from "@/constants/site";
import { useNavigation } from "@/composables/useNavigation";
import { useScrollReveal } from "@/composables/useScrollReveal";

useScrollReveal();
const { navigateToSection } = useNavigation();
const goTo = (id) => navigateToSection(id);
</script>

<style scoped>
.services-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
}

@media (min-width: 768px) {
  .services-row {
    grid-template-columns: repeat(4, 1fr);
  }
}

.service-card {
  padding: 1.75rem 1.5rem;
  text-align: left;
}

.service-card__icon {
  color: var(--color-accent) !important;
  margin-bottom: 1.25rem;
}

.service-card__title {
  font-size: 1rem;
  margin-bottom: 0.375rem;
}

.service-card__count {
  font-size: 0.8125rem;
  color: var(--color-text-muted);
}

.stats-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-bottom: var(--space-section-y);
}

@media (min-width: 768px) {
  .stats-grid {
    grid-template-columns: 1.2fr 1fr;
  }
}

.stats-featured {
  padding: 2.5rem;
}

.stats-featured__number {
  display: block;
  font-size: clamp(4rem, 8vw, 6rem);
  font-weight: 800;
  color: var(--color-accent);
  line-height: 1;
  margin-bottom: 0.5rem;
}

.stats-featured__title {
  margin-bottom: 1rem;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.stats-card {
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.stats-card__value {
  font-size: 2rem;
  font-weight: 800;
  color: var(--color-text);
  line-height: 1;
  margin-bottom: 0.375rem;
}

.stats-card__label {
  font-size: 0.8125rem;
  color: var(--color-text-muted);
}

.services-list-section {
  padding-top: 2rem;
}

.services-list {
  display: flex;
  flex-direction: column;
}

.service-row {
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-template-rows: auto auto;
  gap: 0.5rem 1.5rem;
  align-items: center;
  padding: 2rem 1.5rem;
  border-bottom: 1px solid var(--color-border);
  transition: background var(--transition), padding var(--transition);
  cursor: default;
}

@media (min-width: 768px) {
  .service-row {
    grid-template-columns: auto 200px 1fr auto;
    grid-template-rows: auto;
    padding: 2rem 2rem;
  }
}

.service-row:hover {
  background: var(--color-accent);
  padding-left: 2.5rem;
}

.service-row:hover .service-row__icon-wrap,
.service-row:hover .heading-md,
.service-row:hover .service-row__sub,
.service-row:hover .service-row__desc,
.service-row:hover .service-row__cta {
  color: var(--color-text) !important;
}

.service-row:hover .service-row__icon-wrap {
  border-color: rgba(255, 255, 255, 0.3);
}

.service-row__icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: var(--radius-full);
  border: 1px solid var(--color-border);
  color: var(--color-text-muted);
  transition: all var(--transition);
}

.service-row__sub {
  font-size: 0.8125rem;
  color: var(--color-text-faint);
  display: block;
  margin-top: 0.25rem;
}

.service-row__desc {
  grid-column: 1 / -1;
}

@media (min-width: 768px) {
  .service-row__desc {
    grid-column: auto;
  }
}

.service-row__cta {
  background: none;
  border: none;
  font-family: var(--font-sans);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--color-text);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  white-space: nowrap;
  transition: color var(--transition);
}
</style>
